import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface CyberpunkInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const CyberpunkInsights: React.FC<CyberpunkInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Determine active bullet
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Bullet text entrance animation
  const opacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const scale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.92,
    to: 1,
    config: { damping: 14, stiffness: 100, mass: 0.6 },
  });

  // Card entrance animation (based on first bullet's start)
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 12], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    config: { damping: 15, mass: 0.7, stiffness: 90 },
  });

  // Glitch jitter: brief translateX every 90 frames
  const glitchPhase = globalFrame % 90;
  const glitchX = interpolate(
    glitchPhase,
    [86, 87, 88, 89, 90],
    [0, 4, -4, 2, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  // Progress bar width: grows from 0% to 100% across the bullet's duration
  const bulletDurationMs = activeBullet.endMs - activeBullet.startMs;
  const elapsedMs = Math.max(0, currentMs - activeBullet.startMs);
  const progressPct = Math.min(100, (elapsedMs / bulletDurationMs) * 100);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "rgba(5, 0, 16, 0.92)",
          border: "1px solid rgba(0, 255, 229, 0.4)",
          borderRadius: "4px",
          boxShadow:
            "0 0 30px rgba(0, 255, 229, 0.1), inset 0 0 20px rgba(123, 47, 255, 0.1)",
          padding: "36px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          position: "relative",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
          boxSizing: "border-box",
        }}
      >
        {/* Corner brackets */}
        {/* Top-left */}
        <div
          style={{
            position: "absolute",
            top: -1,
            left: -1,
            width: 16,
            height: 16,
            borderTop: "3px solid #00ffe5",
            borderLeft: "3px solid #00ffe5",
          }}
        />
        {/* Top-right */}
        <div
          style={{
            position: "absolute",
            top: -1,
            right: -1,
            width: 16,
            height: 16,
            borderTop: "3px solid #00ffe5",
            borderRight: "3px solid #00ffe5",
          }}
        />
        {/* Bottom-left */}
        <div
          style={{
            position: "absolute",
            bottom: -1,
            left: -1,
            width: 16,
            height: 16,
            borderBottom: "3px solid #00ffe5",
            borderLeft: "3px solid #00ffe5",
          }}
        />
        {/* Bottom-right */}
        <div
          style={{
            position: "absolute",
            bottom: -1,
            right: -1,
            width: 16,
            height: 16,
            borderBottom: "3px solid #00ffe5",
            borderRight: "3px solid #00ffe5",
          }}
        />

        {/* System label + brand badge */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div
            style={{
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: "12px",
              color: "#00ffe5",
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            {`> PROCESSING NODE 0${activeIndex + 1}`}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#00ffe5"
                accentColor="#00ffe5"
                size={72}
                style="glow"
                opacity={opacity}
              />
            ) : null;
          })()}
        </div>

        {/* Bullet text with glitch jitter */}
        <div
          style={{
            opacity,
            transform: `scale(${scale}) translateX(${glitchX}px)`,
            minHeight: "260px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: "58px",
              fontWeight: 700,
              lineHeight: 1.3,
              color: "#f0f0f0",
              textAlign: "left",
              textTransform: "uppercase",
              letterSpacing: "1px",
              margin: 0,
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 700,
                color: "#00ffe5",
                textShadow: "0 0 15px rgba(0,255,229,0.8)",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>

        {/* Progress bar */}
        <div
          style={{
            width: "100%",
            height: "2px",
            background: "rgba(123, 47, 255, 0.2)",
            borderRadius: "1px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: "100%",
              background: "#7b2fff",
              boxShadow: "0 0 8px rgba(123, 47, 255, 0.6)",
              transition: "width 0.05s linear",
            }}
          />
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
